/* import __COLOCATED_TEMPLATE__ from './combo-input.hbs'; */
var _class, _descriptor, _descriptor2, _descriptor3, _descriptor4;
function _initializerDefineProperty(e, i, r, l) { r && Object.defineProperty(e, i, { enumerable: r.enumerable, configurable: r.configurable, writable: r.writable, value: r.initializer ? r.initializer.call(l) : void 0 }); }
function _defineProperty(e, r, t) { return (r = _toPropertyKey(r)) in e ? Object.defineProperty(e, r, { value: t, enumerable: !0, configurable: !0, writable: !0 }) : e[r] = t, e; }
function _toPropertyKey(t) { var i = _toPrimitive(t, "string"); return "symbol" == typeof i ? i : i + ""; }
function _toPrimitive(t, r) { if ("object" != typeof t || !t) return t; var e = t[Symbol.toPrimitive]; if (void 0 !== e) { var i = e.call(t, r || "default"); if ("object" != typeof i) return i; throw new TypeError("@@toPrimitive must return a primitive value."); } return ("string" === r ? String : Number)(t); }
function _applyDecoratedDescriptor(i, e, r, n, l) { var a = {}; return Object.keys(n).forEach(function (i) { a[i] = n[i]; }), a.enumerable = !!a.enumerable, a.configurable = !!a.configurable, ("value" in a || a.initializer) && (a.writable = !0), a = r.slice().reverse().reduce(function (r, n) { return n(i, e, r) || r; }, a), l && void 0 !== a.initializer && (a.value = a.initializer ? a.initializer.call(l) : void 0, a.initializer = void 0), void 0 === a.initializer ? (Object.defineProperty(i, e, a), null) : a; }
function _initializerWarningHelper(r, e) { throw Error("Decorating class property failed. Please ensure that transform-class-properties is enabled and runs after the decorators transform."); }
import { later } from '@ember/runloop';
import { inject as service } from '@ember/service';
import Component from '@glimmer/component';
import { tracked } from '@glimmer/tracking';
import COUNTRY_ISO_CODES from '@square/ember-phone-number/utils/country-iso-codes';
import { getCountryCallingCode } from 'libphonenumber-js';
import { setInputAutocomplete, setInputEnterKeyHint, setInputEnterListener } from '../utils/input-util';
const DEFAULT_COUNTRY_CODE = 'US';
export const FIELD_MODE = {
  PHONE: 'phone',
  TEXT: 'text'
};
const ACTIONS = Object.freeze({
  COUNTRY_CODE_MANUAL_CHANGE: 'COUNTRY_CODE_MANUAL_CHANGE',
  // Todo: Once we have a way to detect when the country code is changed automatically, we should add this action
  COUNTRY_CODE_AUTOMATIC_CHANGE: 'COUNTRY_CODE_AUTOMATIC_CHANGE',
  FIELD_TYPE: 'FIELD_TYPE'
});
let PhoneNumberInputComponent = (_class = class PhoneNumberInputComponent extends Component {
  constructor(...args) {
    super(...args);
    _initializerDefineProperty(this, "intl", _descriptor, this);
    _initializerDefineProperty(this, "fieldMode", _descriptor2, this);
    _initializerDefineProperty(this, "value", _descriptor3, this);
    _initializerDefineProperty(this, "selectedCountry", _descriptor4, this);
    _defineProperty(this, "marketInputElement", null);
    _defineProperty(this, "inputElement", null);
    _defineProperty(this, "handleSlottedInputInit", element => {
      this.inputElement = element;
      setInputEnterListener(element, this.args?.enterSubmit);
      setInputEnterKeyHint(element, 'go');
      setInputAutocomplete(element, this.args.autocomplete ?? 'username');
      if (this.args.setInput) {
        this.args.setInput(element);
      }
    });
    _defineProperty(this, "handleWrapperClick", () => {
      this.inputElement?.focus();
    });
    _defineProperty(this, "handleCountrySelect", event => {
      const newCountry = event.target.value;
      this.args.trackEvent?.(ACTIONS.COUNTRY_CODE_MANUAL_CHANGE, {
        elementIdentifier: 'mpui-combo-field',
        elementType: 'combo-input',
        previousValue: this.selectedCountry,
        newValue: newCountry
      });
      this.selectedCountry = newCountry;
      this.args.onChange?.(this.outputValue);
    });
    _defineProperty(this, "handleInputChange", event => {
      const inputValue = event.target.value;
      const existingFieldMode = this.fieldMode;
      this.updateFieldMode(inputValue);
      if (existingFieldMode !== this.fieldMode) {
        this.args.trackEvent?.(ACTIONS.FIELD_TYPE, {
          elementIdentifier: 'mpui-combo-field',
          elementType: 'combo-input',
          previousValue: existingFieldMode,
          newValue: this.fieldMode
        });
        this.args.onFieldModeChange?.(this.fieldMode);
      }
      this.value = inputValue;
      this.args.onChange?.(this.outputValue);
    });
    _defineProperty(this, "handleInputBlur", () => {
      this.marketInputElement?.removeAttribute('focused');
    });
    _defineProperty(this, "handleInputFocus", () => {
      this.marketInputElement?.setAttribute('focused', '');
    });
    _defineProperty(this, "handleMarketInputLoad", e => {
      const {
        target: marketInput
      } = e;
      const {
        input
      } = e.detail;
      this.marketInputElement = marketInput;
      // market-input will force focus on shadow input when setting the 'focused' attribute,
      // which will blur the real input.
      // Setting shadow input 'display = none' can make the shadow input un-focusable,
      // which will prevent Safari from shifting the cursor from real input to shadow input.
      input.style.display = 'none';
      if (this.args.autofocus) {
        later(() => {
          this.inputElement.focus();
        });
      }
    });
    _defineProperty(this, "handleAutofill", event => {
      if (event.animationName === 'market-input-autofill-start') {
        document.querySelector(this.inputIdentifier)?.setAttribute('focused', '');
      }
    });
  }
  get tPrefix() {
    return this.args.tPrefix ?? '';
  }
  get fieldInvalid() {
    return !!this.args.errorText;
  }
  get displayCountrySelector() {
    return this.fieldMode === FIELD_MODE.PHONE;
  }
  get selectedCountryFlagEmoji() {
    if (!this.selectedCountry) {
      return '';
    }
    return this.getCountryFlagEmoji(this.selectedCountry);
  }
  get selectedCountryCallingCode() {
    return getCountryCallingCode(this.selectedCountry);
  }
  get countryList() {
    const countryIsoCodes = this.args.includeCountries?.map(country => country.toUpperCase()) ?? COUNTRY_ISO_CODES;
    const countries = countryIsoCodes.map(isoCode => ({
      callingCode: getCountryCallingCode(isoCode),
      flag: this.getCountryFlagEmoji(isoCode),
      isoCode,
      label: this.intl.t(`${this.tPrefix}country_code.${isoCode}`)
    }));
    // return country list sort by label
    return countries.sort((a, b) => {
      if (a.label < b.label) {
        return -1;
      } else if (a.label > b.label) {
        return 1;
      } else {
        return 0;
      }
    });
  }
  get outputValue() {
    if (this.fieldMode === FIELD_MODE.PHONE) {
      const onlyDigitsInput = this.value.replace(/\D/g, '');
      return `+${this.selectedCountryCallingCode}${onlyDigitsInput}`;
    }
    return this.value;
  }
  getCountryFlagEmoji(countryCode) {
    const codePoints = countryCode.toUpperCase().split('').map(char => 127397 + char.charCodeAt());
    return String.fromCodePoint(...codePoints);
  }
  updateFieldMode(inputValue) {
    if (inputValue.length < 2 ||
    // the length is less than 2
    inputValue.match(/\+/i) !== null ||
    // string contains +
    inputValue.match(/@/i) !== null ||
    // string contains @
    inputValue.match(/[a-zA-Z]/i) !== null ||
    // there is letter in the string
    inputValue.match(/\d/i) === null // there is no digits in the string
    ) {
      this.fieldMode = FIELD_MODE.TEXT;
      return;
    }
    // there is any char ascii larger than 127
    for (const char of inputValue) {
      if (char.charCodeAt() > 127) {
        this.fieldMode = FIELD_MODE.TEXT;
        return;
      }
    }
    this.fieldMode = FIELD_MODE.PHONE;
  }
}, _descriptor = _applyDecoratedDescriptor(_class.prototype, "intl", [service], {
  configurable: true,
  enumerable: true,
  writable: true,
  initializer: null
}), _descriptor2 = _applyDecoratedDescriptor(_class.prototype, "fieldMode", [tracked], {
  configurable: true,
  enumerable: true,
  writable: true,
  initializer: function () {
    return FIELD_MODE.TEXT;
  }
}), _descriptor3 = _applyDecoratedDescriptor(_class.prototype, "value", [tracked], {
  configurable: true,
  enumerable: true,
  writable: true,
  initializer: function () {
    return this.args.value ?? '';
  }
}), _descriptor4 = _applyDecoratedDescriptor(_class.prototype, "selectedCountry", [tracked], {
  configurable: true,
  enumerable: true,
  writable: true,
  initializer: function () {
    return this.args.defaultCountry?.toUpperCase() ?? DEFAULT_COUNTRY_CODE;
  }
}), _class);
export { PhoneNumberInputComponent as default };